import React, { useState } from 'react';
import { Modal } from 'react-bootstrap';
import { DoorOpen, Info } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Lease } from '../../types/leases';
import LeaseHistoryButton from '../Leases/LeaseHistoryButton';
import { BOSTADSRATT_RESPONSIBILITY_NOTE, isUnitLeasable, isUnitPlacedOn, isUnitRented, unitDisplayName, unitLevelKeys, unitRoomKeys } from '../../Helpers/unitUtils';
import { RoomImpl } from '../../types/implementations';
import { getAllComponentsForRooms } from '../../Helpers/ComponentRoomUtils';
import { naturalCompare } from '../../Helpers/naturalSort';
import { levelDisplayName } from '../../Helpers/levelDisplayName';
import { Unit } from '../../types/units';
import { UnitKind, UnitTenure } from '../../types/Enums';
import { Building, LevelModel, RoomModel } from '../../types/protocols';
import { translate } from '../../types/EnumTranslations';
import InfoPanel from '../../Components/InfoPanel';
import { RentableDetailSection, RentableDetailTitle, RentableLeaseSection, RentablePhotosSection } from '../../Components/RentableDetail/RentableDetail';
import { useFeature } from '../../hooks/useFeatures';
import ObjectKeysButton from '../KeyRegister/ObjectKeysButton';
import ObjectKeysModal from '../KeyRegister/ObjectKeysModal';

interface UnitDetailProps {
    /**
     * 'modal' (default): a Modal, hidden while `show` is false. 'pane': the same header and sections
     * inline, as the units page's right-most pane on wide screens.
     */
    variant?: 'modal' | 'pane';
    /** Modal only: false while a child modal (Redigera, Nytt hyresavtal, delete confirmation) is open. */
    show?: boolean;
    onHide?: () => void;
    building: Building;
    unit: Unit;
    levels: { [key: string]: LevelModel };
    /** The building's rooms, to resolve the unit's room links (plan D6). */
    rooms?: { [key: string]: RoomModel };
    isAdmin: boolean;
    submitting: boolean;
    onEdit: () => void;
    onDelete: () => void;
    /** The lease behind `unit.currentLeaseKey`, when loaded. */
    lease?: Lease;
    tenantName?: string;
    /** Hyresavtal for this object; absent for non-admins, who cannot open it. */
    leasesLink?: string;
    onNewLease: () => void;
    /** Admins only: the object's ended leases, newest first ("Tidigare hyresgäster"). */
    endedLeases?: Lease[];
    getTenantName: (tenantKey: string) => string;
    /** The unit's trappuppgång, when it has one. */
    stairwellName?: string;
}

const formatArea = (area: number): string => `${area.toLocaleString('sv-SE', { maximumFractionDigits: 1 })} m²`;

/**
 * Details, rooms, photos and actions for the selected unit; Redigera and Ta bort in the header's ⋯ menu.
 * A modal, or the right-most pane of the units page on wide screens.
 */
const UnitDetail: React.FC<UnitDetailProps> = ({
    variant = 'modal', show = true, onHide, building, unit, levels, rooms = {}, isAdmin, submitting,
    onEdit, onDelete,
    lease, tenantName, leasesLink, onNewLease, endedLeases, getTenantName, stairwellName
}) => {
    // Nycklar (admins, key register on): this modal steps aside while the keys modal is open; the register is read on open.
    const keyRegisterOn = useFeature('keyRegister');
    const [keysOpen, setKeysOpen] = useState(false);

    const photos = unit.photos || {};
    const rented = isUnitRented(unit);
    const leasable = isUnitLeasable(unit);
    const isApartment = unit.kind === UnitKind.Apartment;
    const unitLevels = unitLevelKeys(unit, rooms)
        .map(key => levels[key])
        .filter((level): level is LevelModel => !!level)
        .sort((a, b) => b.number - a.number);
    const levelNames = unitLevels.map(level => levelDisplayName(level));
    // Per level the unit is on: at least one of its rooms placed on that level's current drawing, or not (plan D5).
    const placementRows = unitLevels.map(level => {
        const imageKey = level.currentBlueprintKey;
        const drawing = !!imageKey && !!level.blueprints?.[imageKey];
        return { level, drawing, placed: drawing && !!imageKey && isUnitPlacedOn(unit, rooms, imageKey) };
    });
    const placedCount = placementRows.filter(row => row.placed).length;
    // Linked rooms with their components; keys whose room is gone are skipped (plan D6).
    const roomRows = unitRoomKeys(unit)
        .map(key => rooms[key])
        .filter((room): room is RoomModel => !!room)
        .map(room => new RoomImpl(room))
        .sort((a, b) => {
            const la = a.levelKey ? levels[a.levelKey]?.number ?? 0 : 0;
            const lb = b.levelKey ? levels[b.levelKey]?.number ?? 0 : 0;
            return lb - la || naturalCompare(a.number || a.label || '', b.number || b.label || '') || a.key.localeCompare(b.key);
        })
        .map(room => ({
            room,
            level: room.levelKey ? levels[room.levelKey] : undefined,
            components: getAllComponentsForRooms(building, [room.key])
                .sort((a, b) => naturalCompare(a.displayName(building), b.displayName(building))),
        }));
    const placeableCount = placementRows.filter(row => row.drawing).length;

    const title = (
        <RentableDetailTitle
            name={unitDisplayName(unit)}
            status={leasable ? { rented, label: rented ? 'Uthyrd' : 'Ledig' } : undefined}
            onEdit={isAdmin ? onEdit : undefined}
            onDelete={isAdmin ? onDelete : undefined}
            deleteLabel={isApartment ? 'Ta bort lägenhet' : 'Ta bort lokal'}
            disabled={submitting}
        />
    );
    const sections = (
        <>
            <RentableDetailSection icon={<Info size={16} />} title="Uppgifter">
                <InfoPanel
                    items={[
                        { label: 'Typ', value: translate(unit.kind) },
                        { label: 'Upplåtelseform', value: translate(unit.tenure) },
                        ...(isApartment ? [{ label: 'Lgh-nr (Lantmäteriet)', value: unit.officialNumber || '-' }] : []),
                        ...(isApartment ? [{ label: 'Antal rum', value: unit.roomCount ? `${unit.roomCount} rok` : '-' }] : []),
                        ...(!isApartment ? [{ label: 'Typ av lokal', value: unit.commercialType || '-' }] : []),
                        ...(stairwellName ? [{ label: 'Trappuppgång', value: stairwellName }] : []),
                        { label: levelNames.length > 1 ? 'Våningar' : 'Våning', value: levelNames.length > 0 ? levelNames.join(', ') : 'Rummen saknar våning' },
                        { label: `Yta (${isApartment ? 'BOA' : 'LOA'})`, value: unit.area ? formatArea(unit.area) : '-' },
                        {
                            label: 'Placering',
                            value: placeableCount === 0
                                ? 'Ingen ritning'
                                : placementRows.length > 1
                                    ? `Placerad på ${placedCount} av ${placeableCount} plan`
                                    : (placedCount > 0 ? 'Placerad på ritningen' : 'Rummet är inte placerat på ritningen'),
                            valueColor: placeableCount > 0 && placedCount < placeableCount ? '#7a5b00' : undefined,
                        },
                        ...(unit.comment ? [{ label: 'Kommentar', value: unit.comment }] : []),
                    ]}
                />
                {unit.tenure === UnitTenure.Bostadsratt && (
                    <div className="units-note">{BOSTADSRATT_RESPONSIBILITY_NOTE}. Bostadsrätter får inga hyresavtal här.</div>
                )}
                {unit.tenure === UnitTenure.Own && (
                    <div className="units-note">Används av föreningen eller ägaren själv och hyrs inte ut.</div>
                )}
            </RentableDetailSection>

            {leasable && (
                <RentableLeaseSection
                    rented={rented}
                    lease={lease}
                    tenantName={tenantName}
                    leasesLink={leasesLink}
                    rentedText="Uthyrd"
                    vacantText={isApartment ? 'Lägenheten är ledig.' : 'Lokalen är ledig.'}
                    onNewLease={isAdmin ? onNewLease : undefined}
                    submitting={submitting}
                    links={isAdmin && endedLeases && (
                        <LeaseHistoryButton buildingKey={building.key} leases={endedLeases} getTenantName={getTenantName} />
                    )}
                />
            )}

            <RentableDetailSection icon={<DoorOpen size={16} />} title="Rum" count={roomRows.length}>
                {roomRows.length === 0 ? (
                    <p className="text-muted mb-0" style={{ fontSize: '14px' }}>
                        Inga rum kopplade: enheten visas inte på någon ritning. {isAdmin ? 'Koppla rum under Redigera.' : ''}
                    </p>
                ) : (
                    <ul className="units-room-list">
                        {roomRows.map(({ room, level, components }) => (
                            <li key={room.key} className="units-room-row">
                                <div className="units-room-head">
                                    <Link to={`/building/${building.key}/room/${room.key}`}>{room.displayName()}</Link>
                                    {level && <span className="units-badge">{levelDisplayName(level)}</span>}
                                </div>
                                {components.length > 0 && (
                                    <div className="units-room-components">
                                        {components.map(component => (
                                            <Link key={component.key} to={`/building/${building.key}/component/${component.key}`} className="units-component-chip">
                                                {component.displayName(building)}
                                            </Link>
                                        ))}
                                    </div>
                                )}
                            </li>
                        ))}
                    </ul>
                )}
                {placementRows.some(row => row.drawing && !row.placed) && (
                    <div className="units-note">
                        {placementRows.filter(row => row.drawing && !row.placed).map(row => levelDisplayName(row.level)).join(', ')}: inget av enhetens rum är placerat på ritningen. Placera rummen på våningssidan.
                    </div>
                )}
            </RentableDetailSection>

            <RentablePhotosSection
                photos={photos}
                buildingKey={building.key}
            />

            {isAdmin && keyRegisterOn && <ObjectKeysButton onClick={() => setKeysOpen(true)} disabled={submitting} />}
        </>
    );

    return (
        <>
            {variant === 'pane' ? (
                <div className="units-detail-pane">
                    <div className="units-detail-pane-header">{title}</div>
                    {sections}
                </div>
            ) : (
                <Modal show={show && !keysOpen} onHide={onHide} size="lg" centered>
                    <Modal.Header closeButton>{title}</Modal.Header>
                    <Modal.Body>{sections}</Modal.Body>
                </Modal>
            )}
            {isAdmin && keyRegisterOn && keysOpen && (
                <ObjectKeysModal
                    show={show}
                    onHide={() => setKeysOpen(false)}
                    object={{ kind: 'unit', key: unit.key, buildingKey: building.key }}
                    objectName={unitDisplayName(unit)}
                />
            )}
        </>
    );
};

export default UnitDetail;
